import {
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  WritableSignal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { AuthService } from '../auth';
import {
  PatientModel,
  PatientProgramModel,
  UserModel,
} from '../core/api/models';
import { HealthCheckApiService } from '../core/api/services/health-check-api.service';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { StaffApiService, SupervisorApiService } from '../core/api/services';
import { AlertService } from '../shared/services/alert.service';
import { Utils } from '../core/utils';
import { SupervisorHelperService } from './supervisor.helper.service';
import { PatientSessionService } from '../staff/home/patient-session.service';
import { Permissions } from '../core/api/models/permissions';

@Component({
  selector: 'supervisor-main',
  templateUrl: './supervisor-main.component.html',
  styleUrl: './supervisor-main.component.css',
  standalone: false,
})
export class SupervisorMainComponent implements OnInit {
  patientID = input.required<string>();
  patientIDAsNumber: number | null = null;

  private destroyRef = inject(DestroyRef);

  loadingPatients: boolean = false;
  selectedPatient: PatientModel | null = null;

  filteredPatients: any[] = [];

  serverInfo: any = null;
  userInfo: UserModel | null = null;

  pageTitle: string | null = null;
  loading: boolean = true;
  openMenu: boolean = false;
  showMore: boolean = false;
  patientLoadError: string | null = null;

  showAddStaffAlert = this.supervisorHelperService.showAddStaffAlert;
  showAddPlaylist = this.supervisorHelperService.showAddPlaylist;
  showAddTarget = this.supervisorHelperService.showAddTarget;
  showAddSkillArea = this.supervisorHelperService.showAddSkillArea;
  showAddReminder = this.supervisorHelperService.showAddReminder;
  showAddBehavior = this.supervisorHelperService.showAddBehavior;
  showAddProgram = this.supervisorHelperService.showAddProgram;
  addingTargetToProgramID = this.supervisorHelperService.addingTargetToProgramID;

  constructor(
    private authService: AuthService,
    private router: Router,
    protected supervisorHelperService: SupervisorHelperService,
    private activatedRoute: ActivatedRoute,
    protected patientSessionService: PatientSessionService,
    private supervisorApiService: SupervisorApiService,
    private staffApiService: StaffApiService,
    private alertService: AlertService
  ) {
    console.log(
      'SupervisorMain PatientSessionService instance:',
      this.patientSessionService
    );
  }

  ngOnInit() {
    this.authService.user().subscribe((user) => (this.userInfo = user));
    this.serverInfo = 'Loading';
    this.patientIDAsNumber = Number(this.patientID());

    const subscription = this.activatedRoute.params.subscribe((params) => {
      this.patientIDAsNumber = Number(params['patientID']);
      this.patientLoadError = null;
      this.loading = true;
      this.staffApiService
        .GetPatientInfo({ id: this.patientIDAsNumber })
        .subscribe({
          next: (patient) => {
            this.selectedPatient = patient;
            this.loading = false;
            if (this.patientIDAsNumber != null) {
              this.promptForLegacyMaintenanceCriteria(this.patientIDAsNumber);
            }
          },
          error: (error) => {
            console.error('Error loading patient data:', error);
            this.loading = false;
            this.selectedPatient = null;
            this.patientLoadError = this.getPatientLoadErrorMessage(error);
            this.alertService.error(this.patientLoadError);
          }
        });

      if (
        !isNaN(this.patientIDAsNumber) &&
        this.patientIDAsNumber !== this.patientSessionService.getPatientID()
      ) {
        this.patientSessionService.initializePatientSession(
          this.patientIDAsNumber,
          undefined
        );
      }
    });

    this.destroyRef.onDestroy(() => {
      subscription.unsubscribe();
    });
  }

  isRouteActive(route: string): boolean {
    return this.router.url.includes(route);
  }

  onPatientSelected(event: any) {
    this.loading = true;
    this.router.navigate(['/supervisor', this.selectedPatient?.ID]);
  }

  chooseAnotherClient() {
    this.router.navigate(['/select-patient']);
  }

  private getPatientLoadErrorMessage(error: unknown): string {
    const reason = Utils.getAjaxError(error);
    if (reason && reason !== 'Error') {
      return reason;
    }
    return 'This client could not be loaded. You may not have permission to access this patient.';
  }

  private promptForLegacyMaintenanceCriteria(patientID: number): void {
    if (!patientID || this.hasShownLegacyMaintenancePrompt(patientID)) {
      return;
    }

    this.supervisorApiService.GetMaintenanceCriteriaFull({ patientID }).subscribe({
      next: (maintenanceCriteriaFull) => {
        const patientSpecificMaintenanceCriterias =
          maintenanceCriteriaFull.MaintenanceCriterias ?? [];
        const hasActivePatientSpecificMaintenanceCriterias =
          patientSpecificMaintenanceCriterias.some(
            (criteria) =>
              criteria.MaintenanceLevelID != null &&
              !criteria.IsLegacyMaintenanceCriteria
          );
        const hasLegacyMaintenanceCriteria =
          patientSpecificMaintenanceCriterias.some(
            (criteria) => !!criteria.IsLegacyMaintenanceCriteria
          ) && !hasActivePatientSpecificMaintenanceCriterias;

        if (!hasLegacyMaintenanceCriteria) {
          return;
        }

        this.markLegacyMaintenancePromptShown(patientID);
        this.alertService
          .confirm(
            'Legacy maintenance criteria detected',
            'This patient has a legacy custom maintenance criteria configuration. Please visit the maintenance criteria page and save a new custom criteria using the updated three-level flow. Open that page now?'
          )
          .subscribe((confirmed) => {
            if (confirmed) {
              this.router.navigate(['/supervisor', patientID, 'maintanance-criteria']);
            }
          });
      },
      error: () => {
        // Ignore this check so it never blocks the supervisor page.
      },
    });
  }

  private hasShownLegacyMaintenancePrompt(patientID: number): boolean {
    return sessionStorage.getItem(
      `spark-legacy-maintenance-prompt-${patientID}`
    ) === 'true';
  }

  private markLegacyMaintenancePromptShown(patientID: number): void {
    sessionStorage.setItem(
      `spark-legacy-maintenance-prompt-${patientID}`,
      'true'
    );
  }

  switchToStaff() {
    const patientSession = this.patientSessionService.getPatientSession();
    this.router.navigate([
      '/staff/home/' + this.selectedPatient?.ID + '/' + patientSession?.ID,
    ]);
  }

  switchToSessionList() {
    this.router.navigate(['/staff/' + this.selectedPatient?.ID + '/sessions']);
  }

  switchToGraphs() {
    this.router.navigate(['/graphs', this.selectedPatient?.ID]);
  }

  staffAlertButtonClicked() {
    console.log('staffAlertButtonClicked');
    this.supervisorHelperService.staffAlertButtonClicked();
  }

  settingsButtonClicked() {
    console.log('settingsButtonClicked');
    this.router.navigate([
      '/supervisor/' + this.selectedPatient?.ID + '/settings',
    ]);
  }

  onCloseAddSkillArea(withChanges: boolean = false) {
    console.log('onCloseAddSkillArea');
    this.supervisorHelperService.onCloseAddSkillArea(withChanges);
  }

  onCloseAddPlaylist(withChanges: boolean = false) {
    console.log('onCloseAddPlaylist');
    this.supervisorHelperService.onCloseAddPlaylist(withChanges);
  }

  onCloseAddTarget(withChanges: boolean = false) {
    console.log('onCloseAddTarget');
    this.supervisorHelperService.onCloseAddTarget(withChanges);
  }

  onCloseAddStaffAlert(withChanges: boolean = false) {
    console.log('onCloseAddStaffAlert');
    this.supervisorHelperService.onCloseAddStaffAlert(withChanges);
  }

  onCloseAddReminder(withChanges: boolean = false) {
    console.log('onCloseAddReminder');
    this.supervisorHelperService.onCloseAddReminder(withChanges);
  }

  onCloseAddBehavior(withChanges: boolean = false) {
    console.log('onCloseAddBehavior');
    this.supervisorHelperService.onCloseAddBehavior(withChanges);
  }

  onCloseAddProgram(withChanges: boolean = false) {
    console.log('onCloseAddProgram');
    this.supervisorHelperService.onCloseAddProgram(withChanges);
  }

  activated(comp: any) {
    this.pageTitle = comp.pageTitle || '&nbsp;';
    if (comp instanceof SupervisorMainComponent) {
      let tComp = comp as SupervisorMainComponent;
      tComp.userInfo = this.userInfo;
    }
  }

  backToOpto() {
    window.location.href = this.authService.getOptoURL() ?? '/';
  }

  openBIP(setting: string) {
    if (this.selectedPatient && this.selectedPatient.LastBIPDocumentationID != null) {
      
      if (setting == "Home") {
        window.open(this.authService.getOptoURL() + '/TreatmentReports/Documentation/DocumentView/' + this.selectedPatient.LastBIPDocumentationID + '?viewNumber=1', setting);
      }
      if (setting == "Community") {
        window.open(this.authService.getOptoURL() + '/TreatmentReports/Documentation/DocumentView/' + this.selectedPatient.LastBIPDocumentationID + '?viewNumber=2', setting);
      }
      if (setting == "Clinic") {
        window.open(this.authService.getOptoURL() + '/TreatmentReports/Documentation/DocumentView/' + this.selectedPatient.LastBIPDocumentationID + '?viewNumber=3', setting);
      } else {
        window.open(this.authService.getOptoURL() + '/TreatmentReports/Documentation/DocumentView/' + this.selectedPatient.LastBIPDocumentationID, setting);
      }
    } else {
      this.alertService.error("Patient has no BIP.");
    }
  }


  getMoreButtonBackgroundColor(): string {
    return this.isAnyMoreRouteActive() ? '#719eb6' : '#e1e8fb';
  }

  getMoreButtonTextColor(): string {
    return this.isAnyMoreRouteActive() ? 'white' : '#719eb6';
  }

  public isAnyMoreRouteActive(): boolean {
    const routes = [
      'reminders',
      'prompt-codes',
      'prompt-schedules',
      'maintanance-criteria',
      'mastery-criteria',
      'probe-criteria',
      'import-data',
    ];
    return routes.some((route) =>
      this.router.isActive(
        `/supervisor/${this.selectedPatient?.ID}/${route}`,
        true
      )
    );
  }

  openProgramsHelp() {
    console.log('openProgramsHelp');
  }

  hasMergeTargetsPermission(): boolean {
    return this.userInfo?.Permissions?.includes(Permissions.SparkApp_MergeTargets) ?? false;
  }
}
